import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { event as gaEvent } from '../utils/analytics';
import { submitApplicationForm } from '../utils/api';
import { useToast } from '../contexts/ToastContext';

interface FormData {
  fullName: string;
  dateOfBirth: string;
  gender: string;
  contactNumber: string;
  email: string;
  nationality: string;
  address: string;
  education: string;
  passedYear: string;
  certification: string;
  level: string;
  purpose: string;
  language: string;
  shift: string;
  referral: string;
}

const ApplyNow: React.FC = () => {
  const { showToast } = useToast();
  const [currentDate] = useState(new Date().toLocaleDateString());
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    dateOfBirth: '',
    gender: '',
    contactNumber: '',
    email: '',
    nationality: '',
    address: '',
    education: '',
    passedYear: '',
    certification: '',
    level: '',
    purpose: '',
    language: '',
    shift: '',
    referral: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // For proper SEO, set document title on component mount
  useEffect(() => {
    document.title = "Apply Now | Student Application Form | Recall Nepal";
    return () => {
      document.title = "Recall Nepal | Study in Japan Consultancy | Higher Education in Japan";
    };
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'radio') {
      setFormData({
        ...formData,
        [name]: value,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Track form submission attempt
    gaEvent({
      action: 'form_submit_attempt',
      category: 'Application',
      label: 'Student Application Form'
    });
    
    try {
      // Submit form to the backend API
      const response = await submitApplicationForm(formData);
      
      // Show success message using toast
      showToast('Application Submitted Successfully! We will contact you shortly.', 'success');
      
      // Track successful form submission
      gaEvent({
        action: 'form_submit_success',
        category: 'Application',
        label: 'Student Application Form'
      });
      
      // Reset form
      setFormData({
        fullName: '',
        dateOfBirth: '',
        gender: '',
        contactNumber: '',
        email: '',
        nationality: '',
        address: '',
        education: '',
        passedYear: '',
        certification: '',
        level: '',
        purpose: '',
        language: '',
        shift: '',
        referral: '',
      });
    } catch (error: any) {
      console.error('Error submitting application:', error);
      showToast(error.message || 'Something went wrong. Please try again later.', 'error');
      
      // Track form submission error
      gaEvent({
        action: 'form_submit_error',
        category: 'Application',
        label: 'Student Application Form Error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <>
      <Helmet>
        <title>Apply Now | Student Application Form | Recall Nepal</title>
        <meta name="description" content="Apply now to study in Japan. Fill out our easy application form to start your journey towards higher education in Japan with guidance from Recall Nepal." />
        <meta name="keywords" content="apply to study in Japan, Japan student application, university application Japan, Recall Nepal application form" />
        <link rel="canonical" href="https://www.recallnepal.com/apply" />
        <meta property="og:title" content="Apply Now | Student Application Form | Recall Nepal" />
        <meta property="og:description" content="Start your journey to study in Japan. Complete our application form to receive expert guidance and support from Recall Nepal consultancy." />
        <meta property="og:url" content="https://www.recallnepal.com/apply" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "Student Application Form",
              "description": "Application form for students wanting to study in Japan",
              "provider": {
                "@type": "Organization",
                "name": "Recall Nepal",
                "url": "https://www.recallnepal.com"
              },
              "mainEntity": {
                "@type": "FormAction",
                "name": "Student Application Form",
                "target": {
                  "@type": "EntryPoint",
                  "urlTemplate": "https://www.recallnepal.com/apply",
                  "encodingType": "application/x-www-form-urlencoded",
                  "httpMethod": "POST"
                }
              }
            }
          `}
        </script>
      </Helmet>
      <main className="py-20 bg-slate-50 relative overflow-hidden min-h-screen">
        {/* Background elements */}
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
          <motion.div 
            className="absolute -top-40 -right-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl"
            animate={{ 
              x: [0, 30],
              y: [0, -30]
            }}
            transition={{ 
              repeat: Infinity,
              repeatType: "reverse",
              duration: 20,
              ease: "easeInOut" 
            }}
          />
          
          <motion.div 
            className="absolute -bottom-40 -left-40 w-96 h-96 bg-secondary/5 rounded-full blur-3xl"
            animate={{ 
              x: [0, -30],
              y: [0, 30]
            }}
            transition={{ 
              repeat: Infinity,
              repeatType: "reverse",
              duration: 15,
              ease: "easeInOut" 
            }}
          />
        </div>

        {/* Japanese pattern background */}
        <div className="absolute inset-0 overflow-hidden opacity-5 pattern-japanese" aria-hidden="true">
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.header
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <motion.span
              className="text-sm font-medium px-4 py-1.5 rounded-full bg-primary/10 text-primary inline-block mb-4 mt-14"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
            >
              Start Your Journey
            </motion.span>
            <h1 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-black via-black to-black/80 mb-6">
              Student <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">Application</span> Form
            </h1>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              Please fill out the form below to apply for our programs
            </p>
          </motion.header>

          {/* Application Form */}
          <motion.section 
            className="bg-white rounded-xl shadow-xl p-8 md:p-10 max-w-4xl mx-auto mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <div className="flex justify-end mb-8">
              <div className="text-right">
                <p className="text-sm text-gray-500 mb-1">Date</p>
                <p className="text-md font-medium">{currentDate}</p>
              </div>
            </div>

            <form className="space-y-10" onSubmit={handleSubmit} aria-label="Student Application Form">
              {/* Personal Information */}
              <motion.fieldset 
                className="space-y-6"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                viewport={{ once: true }}
              >
                <div className="border-b border-gray-200 pb-2 mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                    Personal Information
                  </h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fullName">Full Name</label>
                    <input 
                      type="text" 
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                      placeholder="Enter your full name"
                      required
                      aria-required="true"
                    />
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="dateOfBirth">Date of Birth (B.S./A.D.)</label>
                    <input 
                      type="text"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                      placeholder="YYYY/MM/DD"
                      required
                      aria-required="true"
                    />
                  </div>
                  
                  <div className="col-span-2 md:col-span-1">
                    <fieldset>
                      <legend className="block text-sm font-medium text-gray-700 mb-1">Gender</legend>
                      <div className="flex space-x-4 mt-2" role="radiogroup" aria-required="true">
                        <label className="inline-flex items-center">
                          <input 
                            type="radio" 
                            name="gender" 
                            value="Male"
                            checked={formData.gender === "Male"}
                            onChange={handleInputChange}
                            className="form-radio h-4 w-4 text-primary" 
                            required
                          />
                          <span className="ml-2">Male</span>
                        </label>
                        <label className="inline-flex items-center">
                          <input 
                            type="radio" 
                            name="gender" 
                            value="Female"
                            checked={formData.gender === "Female"}
                            onChange={handleInputChange}
                            className="form-radio h-4 w-4 text-primary" 
                          />
                          <span className="ml-2">Female</span>
                        </label>
                        <label className="inline-flex items-center">
                          <input 
                            type="radio" 
                            name="gender" 
                            value="Other"
                            checked={formData.gender === "Other"}
                            onChange={handleInputChange}
                            className="form-radio h-4 w-4 text-primary" 
                          />
                          <span className="ml-2">Other</span>
                        </label>
                      </div>
                    </fieldset>
                  </div>
                  
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="contactNumber">Contact Number</label>
                    <input 
                      type="text"
                      id="contactNumber"
                      name="contactNumber"
                      value={formData.contactNumber}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                      placeholder="Enter your contact number"
                      required
                      aria-required="true"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">Email Address</label>
                    <input 
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                      placeholder="Enter your email address"
                      required
                      aria-required="true"
                    />
                  </div>
                  
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="nationality">Nationality</label>
                    <input 
                      type="text"
                      id="nationality"
                      name="nationality"
                      value={formData.nationality}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                      placeholder="Enter your nationality"
                      required
                      aria-required="true"
                    />
                  </div>
                  
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="address">Address</label>
                    <input 
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                      placeholder="Enter your address"
                      required
                      aria-required="true"
                    />
                  </div>
                </div>
              </motion.fieldset>
              
              {/* Education Details */}
              <motion.fieldset 
                className="space-y-6"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                viewport={{ once: true }}
              >
                <div className="border-b border-gray-200 pb-2 mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                    Education Details
                  </h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="education">Last Education Qualification</label>
                    <input 
                      type="text"
                      id="education"
                      name="education"
                      value={formData.education}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                      placeholder="Enter your last qualification"
                      required
                      aria-required="true"
                    />
                  </div>
                  
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="passedYear">Passed Year</label>
                    <input 
                      type="text"
                      id="passedYear"
                      name="passedYear"
                      value={formData.passedYear}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                      placeholder="YYYY"
                      required
                      aria-required="true"
                    />
                  </div>
                </div>
              </motion.fieldset>
              
              {/* Language Proficiency */}
              <motion.fieldset 
                className="space-y-6"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="border-b border-gray-200 pb-2 mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                    Language Proficiency
                  </h2>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="certification">Language Certification (if any)</label>
                    <input 
                      type="text"
                      id="certification"
                      name="certification"
                      value={formData.certification}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                      placeholder="Enter certification details"
                    />
                  </div>
                  
                  <div className="col-span-2 md:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="level">Level</label>
                    <input 
                      type="text"
                      id="level"
                      name="level"
                      value={formData.level}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
                      placeholder="Enter proficiency level"
                    />
                  </div>
                </div>
              </motion.fieldset>
              
              {/* Application Details */}
              <motion.fieldset 
                className="space-y-6"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                viewport={{ once: true }}
              >
                <div className="border-b border-gray-200 pb-2 mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                    Application Details
                  </h2>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Purpose of Application</label>
                    <div className="flex space-x-6">
                      <label className="inline-flex items-center">
                        <input 
                          type="radio" 
                          name="purpose" 
                          value="Study"
                          checked={formData.purpose === "Study"}
                          onChange={handleInputChange}
                          className="form-radio h-4 w-4 text-primary" 
                          required
                        />
                        <span className="ml-2">Study</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input 
                          type="radio" 
                          name="purpose" 
                          value="Apply"
                          checked={formData.purpose === "Apply"}
                          onChange={handleInputChange}
                          className="form-radio h-4 w-4 text-primary" 
                        />
                        <span className="ml-2">Apply</span>
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                    <div className="flex flex-wrap gap-6">
                      <label className="inline-flex items-center">
                        <input 
                          type="radio" 
                          name="language" 
                          value="NAT"
                          checked={formData.language === "NAT"}
                          onChange={handleInputChange}
                          className="form-radio h-4 w-4 text-primary" 
                          required
                        />
                        <span className="ml-2">NAT</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input 
                          type="radio" 
                          name="language" 
                          value="JLPT"
                          checked={formData.language === "JLPT"}
                          onChange={handleInputChange}
                          className="form-radio h-4 w-4 text-primary" 
                        />
                        <span className="ml-2">JLPT</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input 
                          type="radio" 
                          name="language" 
                          value="JLCT"
                          checked={formData.language === "JLCT"}
                          onChange={handleInputChange}
                          className="form-radio h-4 w-4 text-primary" 
                        />
                        <span className="ml-2">JLCT</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input 
                          type="radio" 
                          name="language" 
                          value="JCERT"
                          checked={formData.language === "JCERT"}
                          onChange={handleInputChange}
                          className="form-radio h-4 w-4 text-primary" 
                        />
                        <span className="ml-2">JCERT</span>
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Shift</label>
                    <div className="flex flex-wrap gap-6">
                      <label className="inline-flex items-center">
                        <input 
                          type="radio" 
                          name="shift" 
                          value="Morning"
                          checked={formData.shift === "Morning"}
                          onChange={handleInputChange}
                          className="form-radio h-4 w-4 text-primary" 
                          required
                        />
                        <span className="ml-2">Morning</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input 
                          type="radio" 
                          name="shift" 
                          value="Day"
                          checked={formData.shift === "Day"}
                          onChange={handleInputChange}
                          className="form-radio h-4 w-4 text-primary" 
                        />
                        <span className="ml-2">Day</span>
                      </label>
                      <label className="inline-flex items-center">
                        <input 
                          type="radio" 
                          name="shift" 
                          value="Evening"
                          checked={formData.shift === "Evening"}
                          onChange={handleInputChange}
                          className="form-radio h-4 w-4 text-primary" 
                        />
                        <span className="ml-2">Evening</span>
                      </label>
                    </div>
                  </div>
                </div>
              </motion.fieldset>
              
              {/* Referral */}
              <motion.fieldset 
                className="space-y-6"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="border-b border-gray-200 pb-2 mb-6">
                  <h2 className="text-2xl font-bold text-gray-800 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                    Referral
                  </h2>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">How did you know about this consultancy?</label>
                  <div className="flex flex-wrap gap-6">
                    <label className="inline-flex items-center">
                      <input 
                        type="radio" 
                        name="referral" 
                        value="Friend"
                        checked={formData.referral === "Friend"}
                        onChange={handleInputChange}
                        className="form-radio h-4 w-4 text-primary" 
                        required
                      />
                      <span className="ml-2">Friend</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input 
                        type="radio" 
                        name="referral" 
                        value="Signboards"
                        checked={formData.referral === "Signboards"}
                        onChange={handleInputChange}
                        className="form-radio h-4 w-4 text-primary" 
                      />
                      <span className="ml-2">Signboards</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input 
                        type="radio" 
                        name="referral" 
                        value="Advertisement"
                        checked={formData.referral === "Advertisement"}
                        onChange={handleInputChange}
                        className="form-radio h-4 w-4 text-primary" 
                      />
                      <span className="ml-2">Advertisement</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input 
                        type="radio" 
                        name="referral" 
                        value="Social Media"
                        checked={formData.referral === "Social Media"}
                        onChange={handleInputChange}
                        className="form-radio h-4 w-4 text-primary" 
                      />
                      <span className="ml-2">Social Media</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input 
                        type="radio" 
                        name="referral" 
                        value="Other"
                        checked={formData.referral === "Other"}
                        onChange={handleInputChange}
                        className="form-radio h-4 w-4 text-primary" 
                      />
                      <span className="ml-2">Other</span>
                    </label>
                  </div>
                </div>
              </motion.fieldset>
              
              {/* Submit Button */}
              <motion.div 
                className="pt-6 text-center"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 1 }}
                viewport={{ once: true }}
              >
                <motion.button
                  type="submit"
                  className="inline-block px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white rounded-full font-medium text-lg relative overflow-hidden group shadow-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  disabled={isSubmitting}
                >
                  <span className="relative z-10">
                    {isSubmitting ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Submitting...
                      </span>
                    ) : (
                      "Submit Application"
                    )}
                  </span>
                  <motion.span 
                    className="absolute inset-0 bg-gradient-to-r from-primary via-secondary to-primary"
                    initial={{ x: "-100%" }}
                    whileHover={{ x: "100%" }}
                    transition={{ duration: 1 }}
                  />
                </motion.button>
                <p className="mt-4 text-gray-500 text-sm">
                  By submitting this form, you agree to our terms and privacy policy.
                </p>
              </motion.div>
            </form>
          </motion.section>
        </div>
      </main>
    </>
  );
};

export default ApplyNow; 